import React, { useState } from 'react';
import {
  PencilIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
  UserIcon,
  PaperClipIcon,
  TrashIcon,
  PhotoIcon,
  DocumentTextIcon,
  MusicalNoteIcon,
  FilmIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, CardContent } from '../UI/Card';
import { Button } from '../UI/Button';
import { Textarea } from '../UI/Textarea';
import { Alert, AlertDescription } from '../UI/Alert';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useTicket } from '../../hooks/useTickets';
import { TicketStatus, TicketPriority, TicketCategory, CreateCommentRequest, TicketAttachment } from '../../types/ticket';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/i18nFormat';
import { filesFromClipboardEvent, validateFiles, MAX_FILE_SIZE_BYTES } from '../../utils/imageUpload';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../services/users';

interface TicketViewProps {
  ticketId: number;
  onBack: () => void;
  onEdit: () => void;
}

const statusBadge = {
  [TicketStatus.OPEN]: 'badge-primary',
  [TicketStatus.IN_PROGRESS]: 'badge-warning',
  [TicketStatus.WAITING_CLIENT]: 'badge-secondary',
  [TicketStatus.RESOLVED]: 'badge-success',
  [TicketStatus.CLOSED]: 'badge-secondary',
} as const;

const priorityBadge = {
  [TicketPriority.LOW]: 'badge-success',
  [TicketPriority.MEDIUM]: 'badge-warning',
  [TicketPriority.HIGH]: 'badge-danger',
  [TicketPriority.CRITICAL]: 'badge-danger',
} as const;

// ===== Helpers e componentes no escopo do módulo para estabilidade de identidade =====
const buildAttachmentUrl = (url: string) => {
  try {
    const base = (api.defaults as any)?.baseURL || window.location.origin;
    if (/^https?:\/\//.test(url)) {
      const abs = new URL(url);
      // Migrate v1 URLs to v2
      if (abs.pathname.includes('/api/v1/tickets/')) {
        abs.pathname = abs.pathname.replace('/api/v1/tickets/', '/api/v2/tickets/');
      }
      const isTicketAttachmentPath = abs.pathname.includes('/api/v2/tickets/') && abs.pathname.includes('/attachments/');
      const isLocalhost = abs.hostname === 'localhost';
      if ((isLocalhost || isTicketAttachmentPath) && abs.origin !== window.location.origin) {
        return new URL(abs.pathname + abs.search + abs.hash, window.location.origin).toString();
      }
      return abs.toString();
    }
    const u = new URL(url, base);
    return u.toString();
  } catch (e) {
    return url;
  }
};

const fetchAttachmentBlobUrl = async (path: string): Promise<string> => {
  const token = localStorage.getItem('token');
  const absUrl = buildAttachmentUrl(path);
  const authHeader = (api.defaults.headers.common['Authorization'] as string) || (token ? `Bearer ${token}` : '');
  const resp = await fetch(absUrl, {
    headers: authHeader ? { Authorization: authHeader } : {},
    credentials: 'omit'
  });
  if (!resp.ok) throw new Error('Falha ao carregar arquivo');
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
};

const isAttachmentUrl = (href: string) => {
  try {
    const u = new URL(href, window.location.origin);
    return (u.pathname.includes('/api/v1/tickets/') || u.pathname.includes('/api/v2/tickets/')) && u.pathname.includes('/attachments/');
  } catch {
    return false;
  }
};

const SecuredResource: React.FC<{ path: string; render: (src: string) => React.ReactNode }> = ({ path, render }) => {
  const { t } = useTranslation();
  const [src, setSrc] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchAttachmentBlobUrl(path)
      .then((url) => { if (alive) setSrc(url); })
      .catch(() => { if (alive) setError('Falha ao carregar'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      if (src) URL.revokeObjectURL(src);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  if (loading) return <p className='text-xs text-gray-500 dark:text-gray-400'>{t('tickets.view.attachments.loading')}</p>;
  if (error || !src) return (
    <Alert variant='destructive'>
      <AlertDescription>{t('tickets.view.attachments.error')}</AlertDescription>
    </Alert>
  );
  return <>{render(src)}</>;
};

// Renderizador de imagem para Markdown definido no escopo do módulo
const MarkdownImg: React.FC<{ src?: string; alt?: string }> = ({ src, alt }) => {
  const s = src || '';
  if (s && isAttachmentUrl(s)) {
    return (
      <SecuredResource path={s} render={(secureSrc) => (
        <img src={secureSrc} alt={alt || ''} className='max-w-full h-auto rounded-md cursor-zoom-in' />
      )} />
    );
  }
  return <img src={buildAttachmentUrl(s)} alt={alt || ''} className='max-w-full h-auto cursor-zoom-in' />;
};
const MarkdownComponents = { img: MarkdownImg };

const getAttachmentIcon = (att: TicketAttachment) => {
  const ct = (att.content_type || '').toLowerCase();
  const name = att.filename.toLowerCase();
  const cls = 'h-4 w-4';
  if (ct.startsWith('image/') || /(\.png|jpe?g|gif|webp|bmp)$/i.test(name)) return <PhotoIcon className={cls} />;
  if (ct === 'application/pdf' || /\.pdf$/i.test(name)) return <DocumentTextIcon className={cls} />;
  if (ct.startsWith('audio/') || /(\.mp3|wav|ogg)$/i.test(name)) return <MusicalNoteIcon className={cls} />;
  if (ct.startsWith('video/') || /(\.mp4|webm|mov)$/i.test(name)) return <FilmIcon className={cls} />;
  return <PaperClipIcon className={cls} />;
};

export const TicketView: React.FC<TicketViewProps> = ({
  ticketId,
  onBack,
  onEdit
}) => {
  const { t } = useTranslation();
  const { ticket, comments, attachments, loading, error, addComment, uploadAttachments, updateComment, deleteComment, deleteAttachment, deleteTicket } = useTicket(ticketId);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [commentFilesToUpload, setCommentFilesToUpload] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [commentUploading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [syncingItsm, setSyncingItsm] = useState(false);
  const { user } = useAuth();
  const isAdminUser = user?.role === UserRole.ADMINISTRATOR;

  const handleSyncItsm = async () => {
    if (!ticket) return;
    setSyncingItsm(true);
    try {
      const res = await api.post(`/api/v1/tickets/${ticket.id}/sync-itsm`);
      const { itsm, synced } = res.data;
      if (!synced) {
        toast('Nenhum sistema ITSM configurado');
      } else {
        const parts: string[] = [];
        if (itsm.jira) parts.push(`Jira: ${itsm.jira}`);
        if (itsm.servicenow) parts.push(`ServiceNow: ${itsm.servicenow}`);
        toast.success(parts.join(' | '));
      }
    } catch {
      toast.error('Erro ao sincronizar com ITSM');
    } finally {
      setSyncingItsm(false);
    }
  };

  const statusLabels = {
    [TicketStatus.OPEN]: t('tickets.status.open'),
    [TicketStatus.IN_PROGRESS]: t('tickets.status.in_progress'),
    [TicketStatus.WAITING_CLIENT]: t('tickets.status.waiting_client'),
    [TicketStatus.RESOLVED]: t('tickets.status.resolved'),
    [TicketStatus.CLOSED]: t('tickets.status.closed')
  } as const;

  const priorityLabels = {
    [TicketPriority.LOW]: t('tickets.priority.low'),
    [TicketPriority.MEDIUM]: t('tickets.priority.medium'),
    [TicketPriority.HIGH]: t('tickets.priority.high'),
    [TicketPriority.CRITICAL]: t('tickets.priority.critical')
  } as const;

  const categoryLabels = {
    [TicketCategory.SECURITY_INCIDENT]: t('tickets.category.security_incident'),
    [TicketCategory.FALSE_POSITIVE]: t('tickets.category.false_positive'),
    [TicketCategory.DATA_REQUEST]: t('tickets.category.data_request'),
    [TicketCategory.TECHNICAL_SUPPORT]: t('tickets.category.technical_support'),
    [TicketCategory.FEATURE_REQUEST]: t('tickets.category.feature_request'),
    [TicketCategory.OTHER]: t('tickets.category.other')
  } as const;

  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerSrc, setImageViewerSrc] = useState<string | null>(null);
  const [imageViewerTitle, setImageViewerTitle] = useState<string>('');

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setImageViewerSrc(null);
    setImageViewerTitle('');
  };

  const attachmentsByCommentId = React.useMemo(() => {
    const map: Record<number, TicketAttachment[]> = {};
    attachments.forEach((att) => {
      if (att.comment_id) {
        const key = Number(att.comment_id);
        if (!map[key]) map[key] = [];
        map[key].push(att);
      }
    });
    return map;
  }, [attachments]);

  const ticketLevelAttachments = React.useMemo(() => attachments.filter((att) => !att.comment_id), [attachments]);

  const handleMarkdownClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target && target.tagName === 'IMG') {
      const imgEl = target as HTMLImageElement;
      setImageViewerSrc(imgEl.src);
      setImageViewerTitle('');
      setImageViewerOpen(true);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant='destructive'>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!ticket) {
    return (
      <Alert>
        <AlertDescription>{t('tickets.view.notFound')}</AlertDescription>
      </Alert>
    );
  }

  const openAttachmentWithAuth = async (att: TicketAttachment) => {
    try {
      const path = att.download_url ?? att.url;
      const blobUrl = await fetchAttachmentBlobUrl(path);
      const win = window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      if (!win) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = att.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      alert('Erro ao abrir arquivo. Verifique sua sessão.');
    }
  };

  const handleDeleteAttachment = async (att: TicketAttachment) => {
    if (deletingAttachmentId) return;
    if (!window.confirm('Confirma excluir este arquivo?')) return;
    setDeletingAttachmentId(att.id);
    try {
      await deleteAttachment(att.id);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const DeleteAttachmentButton = ({ att, label }: { att: TicketAttachment; label: string }) => (
    <button
      title={label}
      disabled={deletingAttachmentId === att.id}
      onClick={() => handleDeleteAttachment(att)}
      className='p-1 rounded-md text-danger-500 hover:bg-danger-50 disabled:opacity-50 dark:hover:bg-danger-900/30'
    >
      <TrashIcon className='h-4 w-4' />
    </button>
  );

  const renderAttachmentPreview = (att: TicketAttachment) => {
    const path = att.download_url ?? att.url;
    const ct = (att.content_type || '').toLowerCase();
    const name = att.filename.toLowerCase();
    const deleteLabel = att.comment_id ? 'Excluir arquivo do comentário' : 'Excluir arquivo do ticket';

    const isImage = ct.startsWith('image/') || /(\.png|jpe?g|gif|webp|bmp)$/i.test(name);
    const isPdf = ct === 'application/pdf' || /\.pdf$/i.test(name);
    const isAudio = ct.startsWith('audio/') || /(\.mp3|wav|ogg)$/i.test(name);
    const isVideo = ct.startsWith('video/') || /(\.mp4|webm|mov)$/i.test(name);

    if (isImage) {
      return (
        <div key={att.id} className='mt-1'>
          <SecuredResource path={path} render={(src) => (
            <img
              src={src}
              alt={att.filename}
              className='max-w-full rounded-md cursor-zoom-in'
              style={{ maxHeight: 240 }}
              onClick={() => {
                setImageViewerSrc(src);
                setImageViewerTitle(att.filename);
                setImageViewerOpen(true);
              }}
            />
          )} />
        </div>
      );
    }
    if (isPdf) {
      return (
        <div key={att.id} className='mt-1'>
          <SecuredResource path={path} render={(src) => (
            <iframe src={src} title={att.filename} className='w-full rounded-md' style={{ height: 300 }} />
          )} />
          <div className='flex items-center gap-2 mt-1'>
            <span className='text-xs text-gray-500 dark:text-gray-400'>{att.filename}</span>
            <DeleteAttachmentButton att={att} label={deleteLabel} />
          </div>
        </div>
      );
    }
    if (isAudio) {
      return (
        <div key={att.id} className='mt-1'>
          <SecuredResource path={path} render={(src) => (
            <audio controls src={src} className='w-full' />
          )} />
          <div className='flex items-center gap-2 mt-1'>
            <span className='text-xs text-gray-500 dark:text-gray-400'>{att.filename}</span>
            <DeleteAttachmentButton att={att} label={deleteLabel} />
          </div>
        </div>
      );
    }
    if (isVideo) {
      return (
        <div key={att.id} className='mt-1'>
          <SecuredResource path={path} render={(src) => (
            <video controls src={src} className='w-full rounded-md' style={{ maxHeight: 300 }} />
          )} />
          <div className='flex items-center gap-2 mt-1'>
            <span className='text-xs text-gray-500 dark:text-gray-400'>{att.filename}</span>
            <DeleteAttachmentButton att={att} label={deleteLabel} />
          </div>
        </div>
      );
    }
    return (
      <div key={att.id} className='mt-1 flex items-center gap-2'>
        <button
          onClick={() => openAttachmentWithAuth(att)}
          className='inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline'
        >
          {getAttachmentIcon(att)}
          {att.filename}
        </button>
        <DeleteAttachmentButton att={att} label={deleteLabel} />
      </div>
    );
  };

  const nonImageTicketAttachments = ticketLevelAttachments.filter((att) => {
    const ct = (att.content_type || '').toLowerCase();
    const name = (att.filename || '').toLowerCase();
    return !(ct.startsWith('image/') || /(\.png|jpe?g|gif|webp|bmp)$/i.test(name));
  });

  return (
    <div className='p-6'>
      <p className='text-sm text-gray-500 dark:text-gray-400 mb-1'>
        Tickets / Ticket #{ticket.id}
      </p>
      <div className='flex justify-between items-center mb-6 flex-wrap gap-2'>
        <div className='flex items-center flex-wrap gap-2'>
          <span className={`badge ${statusBadge[ticket.status]}`}>Status: {statusLabels[ticket.status]}</span>
          <span className={`badge ${priorityBadge[ticket.priority]}`}>Prioridade: {priorityLabels[ticket.priority]}</span>
          <span className='badge badge-secondary'>Categoria: {categoryLabels[ticket.category]}</span>
        </div>
        <div className='flex items-center gap-1'>
          <button title='Voltar' onClick={onBack} className='p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'>
            <ArrowLeftIcon className='h-5 w-5' />
          </button>
          {isAdminUser && (
            <>
              <button
                title={syncingItsm ? 'Sincronizando...' : 'Sincronizar com Jira / ServiceNow'}
                onClick={handleSyncItsm}
                disabled={syncingItsm || deletingTicket}
                className='p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700'
              >
                <ArrowPathIcon className={`h-5 w-5 ${syncingItsm ? 'animate-spin' : ''}`} />
              </button>
              <button
                title='Editar'
                onClick={onEdit}
                disabled={deletingTicket || savingEdit}
                className='p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700'
              >
                <PencilIcon className='h-5 w-5' />
              </button>
              <button
                title={deletingTicket ? 'Excluindo...' : 'Excluir'}
                disabled={deletingTicket || savingEdit}
                onClick={async () => {
                  if (!window.confirm('Confirma excluir este ticket?')) return;
                  setDeletingTicket(true);
                  try {
                    await deleteTicket();
                  } finally {
                    setDeletingTicket(false);
                  }
                }}
                className='p-2 rounded-md text-danger-500 hover:bg-danger-50 disabled:opacity-50 dark:hover:bg-danger-900/30'
              >
                <TrashIcon className='h-5 w-5' />
              </button>
            </>
          )}
        </div>
      </div>

      <div className='flex gap-6 flex-col md:flex-row'>
        <div className='flex-[2]'>
          <Card>
            <CardContent>
              <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-3'>{ticket.title}</h2>

              <div className='prose prose-sm dark:prose-invert max-w-none [&_img]:max-w-full [&_img]:h-auto [&_img]:cursor-zoom-in' onClick={handleMarkdownClick}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                  {ticket.description}
                </ReactMarkdown>
              </div>

              {nonImageTicketAttachments.length > 0 && (
                <div className='flex flex-wrap gap-2 mt-2'>
                  {nonImageTicketAttachments.map((att) => (
                    <button
                      key={att.id}
                      onClick={() => openAttachmentWithAuth(att)}
                      className='inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    >
                      {getAttachmentIcon(att)}
                      {att.filename}
                    </button>
                  ))}
                </div>
              )}

              <hr className='my-4 border-gray-200 dark:border-gray-700' />

              <h3 className='text-base font-semibold text-gray-900 dark:text-white mb-3'>
                {t('tickets.view.comments.title')} ({comments.length})
              </h3>

              <div className='space-y-4'>
                {comments.map(comment => (
                  <div key={comment.id} className='flex items-start gap-3'>
                    <div className='h-9 w-9 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center'>
                      <UserIcon className='h-5 w-5 text-gray-500 dark:text-gray-400' />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex justify-between items-center'>
                        <p className='text-sm font-semibold text-gray-900 dark:text-white'>{comment.author?.full_name || 'Usuário'}</p>
                        <p className='text-xs text-gray-500 dark:text-gray-400'>
                          {formatDate(comment.created_at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className='mt-1'>
                        {editingCommentId === comment.id ? (
                          <>
                            <Textarea
                              rows={2}
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                            />
                            <div className='flex justify-end gap-2 mt-1'>
                              <Button
                                size='sm'
                                disabled={savingEdit}
                                onClick={async () => {
                                  if (!editingContent.trim()) return;
                                  setSavingEdit(true);
                                  try {
                                    await updateComment(comment.id, { content: editingContent.trim() });
                                    setEditingCommentId(null);
                                    setEditingContent('');
                                  } finally {
                                    setSavingEdit(false);
                                  }
                                }}
                              >
                                {savingEdit ? t('tickets.view.actions.saving') : t('tickets.view.actions.save')}
                              </Button>
                              <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => {
                                  setEditingCommentId(null);
                                  setEditingContent('');
                                }}
                              >
                                {t('tickets.view.actions.cancel')}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className='text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap'>{comment.content}</p>
                            {attachmentsByCommentId[comment.id]?.length > 0 && (
                              <div className='mt-2 space-y-2'>
                                {attachmentsByCommentId[comment.id].map((att) => (
                                  <div key={att.id} className='border border-gray-200 dark:border-gray-700 rounded-md p-2'>
                                    {renderAttachmentPreview(att)}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className='flex justify-end gap-1 mt-1'>
                              <button
                                title={t('tickets.view.comments.edit')}
                                onClick={() => {
                                  setEditingCommentId(comment.id);
                                  setEditingContent(comment.content);
                                }}
                                className='p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                              >
                                <PencilIcon className='h-4 w-4' />
                              </button>
                              <button
                                title={t('tickets.view.comments.delete')}
                                onClick={async () => {
                                  if (!window.confirm(t('tickets.view.comments.confirmDelete'))) return;
                                  await deleteComment(comment.id);
                                }}
                                className='p-1 rounded-md text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/30'
                              >
                                <TrashIcon className='h-4 w-4' />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {comments.length === 0 && (
                <p className='text-sm text-gray-500 dark:text-gray-400 text-center py-4'>
                  {t('tickets.view.comments.none')}
                </p>
              )}

              <div className='mt-6'>
                <Textarea
                  rows={3}
                  placeholder={t('tickets.view.comments.placeholder')}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onPaste={(e) => {
                    const files = filesFromClipboardEvent(e);
                    if (files.length === 0) return;
                    const hasText = (e.clipboardData?.getData('text') || '').length > 0;
                    if (!hasText) e.preventDefault();
                    const { valid, errors } = validateFiles(files, { acceptImagesOnly: true, maxSizeBytes: MAX_FILE_SIZE_BYTES });
                    if (errors.length > 0) {
                      setAttachmentError(errors.join('\n'));
                    }
                    if (valid.length > 0) {
                      setCommentFilesToUpload(prev => [...prev, ...valid]);
                    }
                  }}
                  disabled={addingComment || commentUploading}
                />
                <div className='flex items-center gap-3 mt-2'>
                  <label className='inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline cursor-pointer'>
                    <PaperClipIcon className='h-4 w-4' />
                    {t('tickets.view.comments.attachFiles')}
                    <input
                      type='file'
                      className='hidden'
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const { valid, errors } = validateFiles(files, { maxSizeBytes: MAX_FILE_SIZE_BYTES });
                        if (errors.length > 0) {
                          setAttachmentError(errors.join('\n'));
                        } else {
                          setAttachmentError(null);
                        }
                        setCommentFilesToUpload(valid);
                      }}
                    />
                  </label>
                  {attachmentError && (
                    <Alert variant='destructive' className='py-1 px-2'>
                      <AlertDescription>{attachmentError}</AlertDescription>
                    </Alert>
                  )}
                  {commentFilesToUpload.length > 0 && (
                    <span className='text-xs text-gray-500 dark:text-gray-400'>{t('tickets.view.comments.filesSelected', { count: commentFilesToUpload.length })}</span>
                  )}
                </div>

                <div className='flex justify-end mt-2'>
                  <Button
                    disabled={addingComment || !newComment.trim()}
                    onClick={async () => {
                      if (!newComment.trim()) return;
                      setAddingComment(true);
                      try {
                        const req: CreateCommentRequest = { content: newComment.trim() };
                        const added = await addComment(req);
                        if (added && commentFilesToUpload.length > 0) {
                          await uploadAttachments(commentFilesToUpload, added.id);
                          setCommentFilesToUpload([]);
                        }
                        setNewComment('');
                      } finally {
                        setAddingComment(false);
                      }
                    }}
                  >
                    <PaperAirplaneIcon className='h-4 w-4 mr-2' />
                    {addingComment ? t('tickets.view.comments.sending') : t('tickets.view.comments.send')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className='flex-1'>
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>{t('tickets.view.info.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.createdBy')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{ticket.created_by?.full_name || '—'}</p>
              </div>
              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.assignee')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{ticket.assigned_to?.full_name || '—'}</p>
              </div>
              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.createdAt')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{formatDate(ticket.created_at)}</p>
              </div>

              <hr className='border-gray-200 dark:border-gray-700' />

              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.status')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{statusLabels[ticket.status]}</p>
              </div>
              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.priority')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{priorityLabels[ticket.priority]}</p>
              </div>
              <div>
                <p className='text-sm text-gray-900 dark:text-white'>{t('tickets.view.info.category')}</p>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{categoryLabels[ticket.category]}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {imageViewerOpen && (
        <div className='fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6'>
          <div className='fixed inset-0 bg-black/50 backdrop-blur-sm' onClick={handleCloseImageViewer} aria-hidden='true' />
          <div className='relative bg-white dark:bg-gray-800 rounded-2xl shadow-glass-lg max-w-4xl w-full mx-auto max-h-[90vh] overflow-y-auto border border-white/20 dark:border-white/10'>
            <div className='p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700'>
              <h3 className='text-sm font-medium text-gray-900 dark:text-white'>{imageViewerTitle}</h3>
              <button onClick={handleCloseImageViewer} className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'>
                ✕
              </button>
            </div>
            <div className='p-4'>
              {imageViewerSrc && <img src={imageViewerSrc} alt={imageViewerTitle} className='w-full h-auto' />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
